import { useCallback, useEffect, useRef, useState } from 'react';

import { useFreeze } from './freezeOwner.js';

/**
 * @file THE CUTSCENE PLAYER: a full-screen still with a line of dialogue under
 * it, advanced by the player, one shot at a time.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT IS A HANDSHAKE, NOT A TIMER
 * ─────────────────────────────────────────────────────────────────────────────
 * `engine/systems/Missions.js` emits `'mission:cutscene'` with a TOKEN and then
 * parks the mission in `BRIEFING` — nothing about it ticks. When the player has
 * read the last shot this component emits `'mission:cutscene:end'` with the
 * same token and the mission starts. That is the whole contract, and it is a
 * handshake rather than a duration for two reasons: a player reads at their own
 * pace, and the director must be able to REJECT a stale answer (a cutscene that
 * outlived its mission, a double-fire) — which is exactly what the token is for.
 *
 * ⚠ THE END EVENT IS EMITTED EXACTLY ONCE PER TOKEN, and that is enforced here
 * as well as in the director. `sentRef` latches it, so a double-click on the
 * last shot cannot skip the mission's first stage even for the one frame before
 * the director's own token check catches it.
 *
 * ⚠ AND THE CHANNEL IS TWO-WAY. `'mission:cutscene:end'` is not only ours to
 * send: the director CANCELS a briefing from its own side when the mission
 * under it fails or the level is torn down. Found the hard way — a scene ended
 * by anything other than a click left the still image on screen over a frozen
 * loop with nothing left to answer it, which is unrecoverable. So this listens
 * to the channel it writes to, filtered by `sentRef` so it never hears itself.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT FREEZES THE SIM THE SAME WAY THE PAUSE MENU DOES
 * ─────────────────────────────────────────────────────────────────────────────
 * `engine.loop.setTimeScale(0)`, not `loop.stop()` — see `PauseMenu.jsx`'s
 * header for why (the world keeps being drawn and the canvas keeps resizing;
 * only the fixed step stops). The effect's CLEANUP always restores 1, so
 * unmounting mid-cutscene can never leave a live engine frozen.
 *
 * ⚠ TWO THINGS CAN FREEZE THE LOOP NOW, and the last one to restore wins. That
 * is safe in the order the game can actually produce them — Esc is swallowed
 * here, so a cutscene cannot be open at the same time as the pause menu — and
 * it is why this component swallows Esc rather than passing it through: a pause
 * menu opened over a cutscene would restore the time scale to 1 on its own
 * cleanup and leave the game running behind a still image.
 *
 * ⚠ AND IT CLEARS THE INPUT, entering and leaving, for the same reason the
 * pause menu does: a key held when the briefing opened would otherwise still be
 * held when it closes and the player walks off on their own.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ART IS OPTIONAL
 * ─────────────────────────────────────────────────────────────────────────────
 * A shot with no `image` is a dialogue card on the letterbox — which is what
 * every mission that is not the illustrated intro uses, and what a missing file
 * degrades to. `onError` clears the image rather than leaving a broken-image
 * glyph on a story beat.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 */

/** Keys that advance the scene. Enter and Space are the ones people try. */
const NEXT_KEYS = new Set(['Space', 'Enter', 'NumpadEnter', 'KeyE']);

export default function Cutscene({ engine }) {
  /** @type {[null|{token: string, shots: any[], name?: string}, Function]} */
  const [scene, setScene] = useState(null);
  const [shot, setShot] = useState(0);
  const [broken, setBroken] = useState(false);
  /** Latches the end emit, so one token can only ever be answered once. */
  const sentRef = useRef('');
  const sceneRef = useRef(null);
  const shotRef = useRef(0);

  sceneRef.current = scene;
  shotRef.current = shot;

  // ── subscribe ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const bus = engine?.bus;
    if (!bus?.on) return undefined;
    const offOpen = bus.on('mission:cutscene', (p) => {
      if (!p?.shots?.length || !p.token) return;
      setScene(p);
      setShot(0);
      setBroken(false);
    });
    // ⚠ AND THE END EVENT, WHICH WE DO NOT ONLY SEND. The director cancels a
    // briefing from its own side when the mission under it fails or the level
    // is torn down (`Missions.js#cancelCutscene`) — and without this the still
    // image sits on screen over a frozen loop with nothing left to answer it.
    // Our own emits are filtered by `sentRef`, so this can only ever fire for
    // somebody else's.
    const offEnd = bus.on('mission:cutscene:end', (p) => {
      const token = typeof p === 'string' ? p : p?.token;
      if (!token || sentRef.current === token) return;
      setScene((s) => (s && s.token === token ? null : s));
    });
    return () => {
      offOpen?.();
      offEnd?.();
    };
  }, [engine]);

  // A new engine means the old world is gone — never leave a still image from a
  // disposed level on screen.
  useEffect(() => {
    setScene(null);
    setShot(0);
    sentRef.current = '';
  }, [engine]);

  // ── freeze the sim + clear held keys ───────────────────────────────────────
  // ⚠ THE FREEZE GOES THROUGH `freezeOwner.js` AND NOT THROUGH `setTimeScale`.
  // The header above says "two things can freeze the loop now, and the last one
  // to restore wins" and treats that as safe because Esc is swallowed here. It
  // is not safe any more — there are FIVE overlays now — and the claim is
  // reference-counted instead, so this component's cleanup can only restart the
  // world if nothing else is still holding it. Keyed on a BOOLEAN, never on
  // `scene`: a payload object would tear the claim down and rebuild it on every
  // republish.
  const playing = !!scene;
  useFreeze(engine, 'cutscene', playing);
  useEffect(() => {
    if (!playing) return undefined;
    clearGameInput(engine);
    return () => clearGameInput(engine);
  }, [engine, playing]);

  // ── advance ────────────────────────────────────────────────────────────────
  const advance = useCallback(() => {
    const s = sceneRef.current;
    if (!s) return;
    if (shotRef.current + 1 < s.shots.length) {
      setShot((k) => k + 1);
      setBroken(false);
      return;
    }
    // Last shot: answer the director, exactly once for this token.
    if (sentRef.current !== s.token) {
      sentRef.current = s.token;
      engine?.bus?.emit?.('mission:cutscene:end', { token: s.token });
    }
    setScene(null);
    setShot(0);
  }, [engine]);

  /** Straight to the end — the director gets the same single end event. */
  const skip = useCallback(() => {
    const s = sceneRef.current;
    if (!s) return;
    if (sentRef.current !== s.token) {
      sentRef.current = s.token;
      engine?.bus?.emit?.('mission:cutscene:end', { token: s.token });
    }
    setScene(null);
    setShot(0);
  }, [engine]);

  // ── keyboard ───────────────────────────────────────────────────────────────
  // Capture phase on `window`, and `stopImmediatePropagation` — the same rule
  // `PauseMenu.jsx` documents: `engine/input/Keyboard.js` listens on the same
  // node in the bubble phase, so plain `stopPropagation` would not keep a key
  // out of the sim.
  useEffect(() => {
    if (!scene) return undefined;
    const onKeyDown = (e) => {
      if (e.code === 'Escape') {
        // Swallowed, never forwarded: a pause menu opened over a cutscene would
        // restore the time scale on its own cleanup and leave the world running
        // behind a still image. Esc skips the scene instead.
        e.preventDefault();
        e.stopImmediatePropagation();
        skip();
        return;
      }
      if (NEXT_KEYS.has(e.code)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        advance();
        return;
      }
      // Everything else is swallowed too — WASD during a briefing must not
      // survive into the first step of the mission.
      e.stopImmediatePropagation();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [scene, advance, skip]);

  if (!scene) return null;

  const cur = scene.shots[Math.min(shot, scene.shots.length - 1)] ?? {};
  const last = shot >= scene.shots.length - 1;

  return (
    <div
      data-hud-overlay=""
      onMouseDown={(e) => e.preventDefault()}
      onClick={advance}
      className="hud-fade-in pointer-events-auto absolute inset-0 z-[60] flex cursor-pointer flex-col bg-void"
    >
      {/* THE STILL. `object-contain` on a black field, not `cover`: the art is
          authored 16:9 and cropping a character's face out of a story beat to
          fill an ultrawide is the wrong trade. */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center">
        {cur.image && !broken ? (
          <img
            src={cur.image}
            alt=""
            draggable={false}
            onError={() => setBroken(true)}
            className="max-h-full max-w-full select-none object-contain"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <div className="font-display text-4xl uppercase tracking-[0.3em] text-hud-faint/40">
              {scene.name ?? 'Briefing'}
            </div>
          </div>
        )}

        {/* Letterbox gradient, so the caption never fights the art under it. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-void to-transparent" />
      </div>

      {/* THE LINE. Fixed height so a one-line and a four-line shot do not make
          the image jump between frames. */}
      <div className="relative z-10 -mt-24 px-6 pb-8 sm:px-16">
        <div className="mx-auto max-w-4xl">
          {cur.speaker ? (
            <div className="mb-2 font-display text-lg uppercase tracking-[0.2em] text-gold">
              {cur.speaker}
            </div>
          ) : null}
          <p className="min-h-[4.5rem] font-hud text-[17px] leading-relaxed text-hud sm:text-[19px]">
            {cur.text}
          </p>

          <div className="mt-5 flex items-center justify-between">
            {/* Shot pips: three frames should read as three frames. */}
            <div className="flex gap-1.5" aria-hidden="true">
              {scene.shots.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 w-7 rounded-sm transition ${i <= shot ? 'bg-gold' : 'bg-hairline'}`}
                />
              ))}
            </div>

            <div className="flex items-center gap-4">
              {!last ? (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    skip();
                  }}
                  className="font-hud text-[11px] uppercase tracking-[0.28em] text-hud-faint transition hover:text-hud-dim"
                >
                  Skip
                </button>
              ) : null}
              <span className="font-hud text-[11px] uppercase tracking-[0.28em] text-hud-faint">
                {last ? 'Click / Space — begin' : 'Click / Space'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Drops any key the sim thinks is held. Reached through `engine.systems`, the
 * same way `PauseMenu.jsx` does it — `createGameplay` already exposes its
 * `Input`, so this needs no new engine surface.
 * @param {any} engine
 */
function clearGameInput(engine) {
  const systems = engine?.systems;
  if (!Array.isArray(systems)) return;
  for (const s of systems) {
    if (s?.name === 'gameplay') s.input?.clear?.();
  }
}
